import React, { useRef, useEffect } from 'react'
import { Group, Text, Rect, Transformer } from 'react-konva'
import { getPlaceholderText, calculateAutoFitFontSize } from './canvasUtils'

// Corner anchors that should scale text proportionally
const CORNER_ANCHORS = ['top-left', 'top-right', 'bottom-right', 'bottom-left']

/**
 * TransformableText - A draggable, resizable text element for the canvas
 */
export default function TransformableText({
  field,
  isSelected,
  onSelect,
  onChange,
  onDoubleClick,
  onContextMenu,
  onDragStart,
  onDragMove,
  registerRef,
  hideBoundingBoxes
}) {
  const groupRef = useRef()
  const textRef = useRef()
  const trRef = useRef()
  const activeAnchorRef = useRef(null)

  useEffect(() => {
    if (isSelected && trRef.current && groupRef.current) {
      trRef.current.nodes([groupRef.current])
      trRef.current.getLayer().batchDraw()
    }
  }, [isSelected])

  // Register group ref with parent for group move
  useEffect(() => {
    if (registerRef) registerRef(groupRef.current)
    return () => { if (registerRef) registerRef(null) }
  }, [registerRef])

  const displayText = field.sampleText || field.text || field.defaultPlaceholder || getPlaceholderText(field.label) || field.label || 'Text'
  const width = field.width || 100
  const height = field.height || 30

  // Calculate effective font size with auto-fit
  const baseFontSize = field.textSize || 16
  const minFontSize = field.minFontSize || 8
  const effectiveFontSize = field.autoFit
    ? calculateAutoFitFontSize(displayText, width, height, baseFontSize, minFontSize, field.fontName, field.wrap, field.spacing || 0)
    : baseFontSize

  return (
    <>
      <Group
        ref={groupRef}
        x={field.left || 0}
        y={field.top || 0}
        width={width}
        height={height}
        rotation={field.rotation || 0}
        clipFunc={(ctx) => {
          var pad = Math.ceil((field.textSize || 16) * 0.5)
          ctx.rect(-pad, -pad, width + pad * 2, height + pad * 2)
        }}
        draggable
        onClick={onSelect}
        onTap={onSelect}
        onDblClick={onDoubleClick}
        onDblTap={onDoubleClick}
        onContextMenu={onContextMenu}
        onDragStart={(e) => {
          if (onDragStart) onDragStart(field.id)
        }}
        onDragMove={(e) => {
          if (onDragMove) {
            const pos = { x: e.target.x(), y: e.target.y() }
            const snapped = onDragMove(field.id, pos)
            if (snapped) {
              e.target.x(snapped.x)
              e.target.y(snapped.y)
            }
          }
        }}
        onDragEnd={(e) => {
          onChange({
            ...field,
            left: Math.round(e.target.x()),
            top: Math.round(e.target.y()),
          })
        }}
        onTransformStart={(e) => {
          // Track which anchor started the transform
          if (trRef.current) {
            activeAnchorRef.current = trRef.current.getActiveAnchor()
          }
        }}
        onTransform={(e) => {
          // Rotation drag: scaleX/scaleY stay 1, nothing to counteract.
          if (activeAnchorRef.current === 'rotater') return

          // During transform, counteract scaling for side anchors to prevent text distortion
          const node = groupRef.current
          const text = textRef.current
          if (!node || !text) return

          const isCornerResize = CORNER_ANCHORS.includes(activeAnchorRef.current)

          if (!isCornerResize) {
            // Side resize: counter-scale the text to prevent distortion
            const scaleX = node.scaleX()
            const scaleY = node.scaleY()
            text.scaleX(1 / scaleX)
            text.scaleY(1 / scaleY)
            // Also update text width/height to fill the new box size
            text.width(width * scaleX)
            text.height(height * scaleY)
          }
          // Corner resize: let text scale naturally (no counter-scaling)
        }}
        onTransformEnd={(e) => {
          const node = groupRef.current
          const text = textRef.current
          const scaleX = node.scaleX()
          const scaleY = node.scaleY()

          // Reset scale on group and text
          node.scaleX(1)
          node.scaleY(1)
          if (text) {
            text.scaleX(1)
            text.scaleY(1)
          }

          const newWidth = Math.round(Math.max(30, width * scaleX))
          const newHeight = Math.round(Math.max(15, height * scaleY))

          const updates = {
            ...field,
            left: Math.round(node.x()),
            top: Math.round(node.y()),
            width: newWidth,
            height: newHeight,
            // Konva's rotate handle keeps the box's on-screen center fixed and
            // adjusts x/y for us -- rotation is just whatever it left behind.
            rotation: Math.round(node.rotation() * 100) / 100,
          }

          // Only scale text size when using corner anchors (proportional resize)
          const isCornerResize = CORNER_ANCHORS.includes(activeAnchorRef.current)
          if (isCornerResize) {
            // Use the average scale factor for text size
            const scale = (scaleX + scaleY) / 2
            const currentSize = field.textSize || 16
            updates.textSize = Math.round(Math.max(8, Math.min(200, currentSize * scale)))
          }

          activeAnchorRef.current = null
          onChange(updates)
        }}
      >
        {/* Bounding box - only show when not hidden */}
        {!hideBoundingBoxes && (
          <Rect
            x={0}
            y={0}
            width={width}
            height={height}
            stroke={isSelected ? '#4a90d9' : '#888888'}
            strokeWidth={1}
            dash={isSelected ? [] : [4, 4]}
          />
        )}
        {/* Text content */}
        <Text
          ref={textRef}
          x={0}
          y={0}
          width={width}
          height={height}
          text={displayText}
          fontSize={effectiveFontSize}
          fontFamily={field.fontName || 'Arial'}
          fontStyle={field.fontStyle || 'normal'}
          textDecoration={field.textDecoration || ''}
          fill={field.color || '#000000'}
          letterSpacing={field.spacing || 0}
          lineHeight={1.2}
          align={field.align || 'left'}
          verticalAlign={field.verticalAlign || 'middle'}
          wrap={field.wrap === false ? 'none' : 'word'}
          stroke={field.strokeWidth > 0 ? (field.strokeColor || '#000000') : undefined}
          strokeWidth={field.strokeWidth > 0 ? field.strokeWidth : undefined}
          fillAfterStrokeEnabled={field.strokeWidth > 0}
        />
      </Group>
      {isSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(oldBox, newBox) => {
            // Limit resize
            if (newBox.width < 30 || newBox.height < 15) {
              return oldBox
            }
            return newBox
          }}
          anchorSize={8}
          anchorCornerRadius={1}
          anchorStroke="#4a90d9"
          anchorFill="#ffffff"
          borderStroke="#4a90d9"
          borderStrokeWidth={1}
          enabledAnchors={['top-left', 'top-center', 'top-right', 'middle-right', 'bottom-right', 'bottom-center', 'bottom-left', 'middle-left']}
          rotateEnabled={true}
          rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
          rotateAnchorOffset={24}
        />
      )}
    </>
  )
}
